/**
 * Optional entry point for the conversation shell: start a new conversation or
 * continue an existing one. Hosts that always open a specific conversation
 * (e.g. a pre-bound agent run) can skip this component entirely.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { readConversationTitle } from './conversationDisplayName';
import type { AiStudioConversationSummary } from '@/types/aiStudio';

interface ConversationPickerProps {
  conversations: AiStudioConversationSummary[];
  loading?: boolean;
  creating?: boolean;
  onCreate: () => void;
  onSelect: (conversationId: string) => void;
  className?: string;
}

export const ConversationPicker: React.FC<ConversationPickerProps> = ({
  conversations,
  loading = false,
  creating = false,
  onCreate,
  onSelect,
  className = '',
}) => {
  const { t } = useLanguage();

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <button
        type="button"
        onClick={onCreate}
        disabled={creating}
        className="w-fit rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
      >
        {creating ? t('aiStudio.conversation.creating') : t('aiStudio.conversation.newConversation')}
      </button>

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.conversation.loadingConversations')}
        </p>
      ) : conversations.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.conversation.noConversations')}
        </p>
      ) : (
        <div>
          <p className="mb-2 text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.conversation.recentConversations')}
          </p>
          <ul className="space-y-1">
            {conversations.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => onSelect(conversation.id)}
                  className="w-full rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-3 py-2 text-left hover:bg-[var(--color-surface-hover)]"
                >
                  <span className="block truncate font-medium text-[var(--color-surface-text)]">
                    {readConversationTitle(conversation.shellMetadata, conversation.name) ||
                      t('aiStudio.conversation.untitled')}
                  </span>
                  {conversation.messageCount !== undefined && (
                    <span className="block text-base text-[var(--color-surface-text-secondary)]">
                      {t('aiStudio.conversation.messageCount', { count: conversation.messageCount })}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
